import * as THREE from "three";
import { ModelLoader } from "./ModelLoader";

/**
 * Loads character GLBs and patches them so they render properly:
 *
 *  1. The shipped GLBs were generated by TripoSR and ship with noisy/muddy
 *     vertex colors (COLOR_0) that read as "AI splotch". We strip them
 *     and apply a clean per-role solid tint instead — keeps the geometry's
 *     character (silhouette, pose, build) while losing the AI color soup.
 *
 *  2. TripoSR doesn't emit per-vertex normals, so directional lighting
 *     can't shade the surface. We compute them with computeVertexNormals.
 *
 *  3. TripoSR centres the mesh around its bbox centre (feet at -0.5, head
 *     at +0.5 roughly), so placing the model at world y=0 buries it half
 *     in the floor. We translate up by -bbox.min.y so feet land on y=0.
 */

/** Solid material tint per character id. Distinguishes roles at a glance
 * without depending on the noisy generated vertex colors. */
const TINT_BY_ID: Record<string, number> = {
  chef:    0xf2ece0, // off-white apron
  waiter:  0x4a5a78, // dark slate uniform
  errand:  0x8aa67b, // sage canvas
  // Barman shares the waiter GLB (same silhouette) but with a deep
  // black uniform tint so the player can tell them apart at a glance.
  barman:  0x161617,
  "guest-v0": 0xc69a7a,
  "guest-v1": 0xb8a07c,
  "guest-v2": 0xa88c70,
  "guest-v3": 0xd6b89a,
  "guest-v4": 0x9b8268,
  "guest-v5": 0xc2a988,
  "guest-v6": 0xae9684,
};
const DEFAULT_TINT = 0xe6c9a0;

/** Character ids that re-use another character's GLB instead of
 * shipping their own. Used so we don't have to author a second mesh
 * for the barman — they wear the waiter's geometry with a different
 * tint applied via TINT_BY_ID. Keyed by the requested id, value is
 * the GLB filename stem actually loaded. */
const GLB_ALIAS: Record<string, string> = {
  barman: "waiter",
};

export class CharacterLoader {
  private readonly modelLoader: ModelLoader;
  private readonly characterScale: number;

  constructor(modelLoader: ModelLoader, characterScale = 1.7) {
    this.modelLoader = modelLoader;
    this.characterScale = characterScale;
  }

  async load(characterId: string): Promise<THREE.Group> {
    // Resolve alias roles (e.g. barman → waiter GLB). Tint is still
    // looked up by the REQUESTED id so the alias gets its own colour.
    const glbId = GLB_ALIAS[characterId] ?? characterId;
    const root = await this.modelLoader.load(`assets/characters/${glbId}.glb`);
    this.patchMaterials(root, characterId);
    this.computeNormalsIfMissing(root);
    // Scale FIRST so bbox computation reflects the rendered size, THEN
    // lift so the (scaled) feet land at y=0. Doing this in the other
    // order leaves the model partially sunk into the floor.
    //
    // Orientation note: previous commit tried a -π/2 child rotation to
    // "fix" crab walking, but that ended up rotating some characters
    // by the WRONG axis and breaking guests + the helper. The actual
    // fix lives in the routers' moveActor functions (formula change to
    // atan2(-dz, dx)) and PedestrianSpawner — the GLB's authored
    // forward stays +X and the formula compensates.
    root.scale.setScalar(this.characterScale);
    this.liftFeetToOrigin(root);
    return root;
  }

  /** Anti-perf — one shared MeshStandardMaterial per characterId, built
   * lazily and cached for the process lifetime. Characters spawn + despawn
   * constantly (guests, pedestrians), and the old code allocated a FRESH
   * material on every spawn that no despawn path disposed. That leaked GPU
   * memory and — worse — forced a synchronous shader compile the first time
   * each new material was rendered, so changing to a higher floor (which
   * reveals every accumulated character at once) stalled the main thread
   * harder the longer the session ran. There are only a handful of
   * characterIds and nothing mutates a character's material per-instance,
   * so sharing is safe and caps the shader-program count to the asset set. */
  private static readonly materialCache = new Map<string, THREE.MeshStandardMaterial>();

  private static sharedMaterial(characterId: string): THREE.MeshStandardMaterial {
    let mat = CharacterLoader.materialCache.get(characterId);
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({
        vertexColors: false,
        color: TINT_BY_ID[characterId] ?? DEFAULT_TINT,
        roughness: 0.78,
        metalness: 0.0,
        // TripoSR meshes are single-sided but the back face is often the
        // "hollow" side — render both sides so we don't see through them.
        side: THREE.DoubleSide,
      });
      CharacterLoader.materialCache.set(characterId, mat);
    }
    return mat;
  }

  /** Replace whatever material the GLB ships with by a clean solid-tint
   * MeshStandardMaterial (shared per characterId — see materialCache).
   * Strips vertex colors so the noisy TripoSR splotch doesn't leak through
   * — the geometry alone reads the character. */
  private patchMaterials(root: THREE.Object3D, characterId: string): void {
    const mat = CharacterLoader.sharedMaterial(characterId);
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const geom = obj.geometry as THREE.BufferGeometry;
      // Actively REMOVE the vertex color attribute so even materials that
      // default to honoring it can't pick it up later.
      if (geom.getAttribute("color")) {
        geom.deleteAttribute("color");
      }
      obj.material = mat;
      obj.castShadow = true;
      obj.receiveShadow = true;
    });
  }

  private computeNormalsIfMissing(root: THREE.Object3D): void {
    root.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;
      const geom = obj.geometry as THREE.BufferGeometry;
      if (!geom.getAttribute("normal")) {
        geom.computeVertexNormals();
      }
    });
  }

  private liftFeetToOrigin(root: THREE.Object3D): void {
    // World matrix may not be up to date yet — force compute.
    root.updateWorldMatrix(true, true);
    const bbox = new THREE.Box3().setFromObject(root);
    if (Number.isFinite(bbox.min.y)) {
      root.position.y -= bbox.min.y;
    }
  }
}
